import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { AuthService } from '../../../../../../services/auth.service';
import { Subject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ChatMessage, ChatView, suggestionChipLabels, copilotUnavailableMessageKey } from '../../ai-counselor.model';
import { MessageBubbleComponent } from '../message-bubble/message-bubble.component';
import { SuggestionChipsComponent } from '../suggestion-chips/suggestion-chips.component';
import { ChatInputComponent } from '../chat-input/chat-input.component';
import { getGreetingKey, GreetingKey } from '../../../../../../utils/greeting.util';
import { UserRole } from '../../../../../../core/constants/keys.constant';

@Component({
  selector: 'mindbase-chat-window',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MessageBubbleComponent,
    SuggestionChipsComponent,
    ChatInputComponent
  ],
  templateUrl: './chat-window.component.html',
  styleUrl: './chat-window.component.scss'
})
export class ChatWindowComponent implements OnChanges, AfterViewChecked, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private shouldScroll = false;
  /** Height before older messages were prepended — used to restore scroll position. */
  private prevScrollHeight = 0;
  /** True while waiting for prepended messages to be rendered. */
  private restoreScrollAfterLoad = false;
  /** Prevents scroll-triggered load-more until after the initial render + scroll-to-bottom. */
  private scrollLoadEnabled = false;
  private scrollEnableTimer: ReturnType<typeof setTimeout> | null = null;

  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;

  @Input() view: ChatView = 'welcome';
  @Input() messages: ChatMessage[] = [];
  @Input() userName = '';
  @Input() isSending = false;
  /** True while the chatbot is unavailable (GET /user/chatbot disable window) — disables the input. */
  @Input() disabled = false;
  /** "You will be able to use the chatbot after <date>." — shown as the input placeholder while `disabled`. */
  @Input() blockedMessage = '';
  @Input() hasMoreMessages = false;
  @Input() isLoadingMore = false;
  @Output() sendMessage = new EventEmitter<string>();
  @Output() suggestionSelected = new EventEmitter<string>();
  @Output() loadMore = new EventEmitter<void>();

  readonly botAvatarUrl = 'assets/images/ai-counselor-avatar.svg';
  readonly chatBotAvatarUrl = 'assets/images/ai-counselor-avatar.svg';

  get greetingKey(): GreetingKey {
    return getGreetingKey();
  }

  /** Tutors/counsellors are guiding a student's plans, not their own — swap the closing clause accordingly. */
  get letsFindKey(): string {
    return this.isTutorOrCounselor ? 'LetsFindTutor' : 'LetsFind';
  }

  get suggestions(): string[] {
    return suggestionChipLabels(this.isTutorOrCounselor);
  }

  private get isTutorOrCounselor(): boolean {
    const role = this.authService.currentProfileDetails?.role;
    return role === UserRole.TUTOR || role === UserRole.COUNSELOR;
  }

  /** Tutors/counsellors have no tutor/counsellor of their own to fall back on — they're pointed to admin instead. */
  get unavailableMessageKey(): string {
    return copilotUnavailableMessageKey(this.isTutorOrCounselor);
  }

  get hasStreamingContent(): boolean {
    return this.messages.some(m => !!m.isStreaming && !!m.content);
  }

  get userAvatarUrl(): string {
    return this.authService.currentProfileDetails?.image ?? '';
  }

  constructor(
    private readonly ngZone: NgZone,
    private readonly authService: AuthService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['messages']) {
      if (this.restoreScrollAfterLoad) {
        // Older messages prepended — restore position after render
        this.restoreScrollAfterLoad = false;
      } else {
        // New session selected or new message — disable scroll-load until settled
        this.scrollLoadEnabled = false;
        if (this.scrollEnableTimer) clearTimeout(this.scrollEnableTimer);
        this.shouldScroll = true;
      }
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    } else if (this.prevScrollHeight > 0) {
      // Restore scroll so the viewport stays at the same message
      const el = this.messagesContainer?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight - this.prevScrollHeight;
        this.prevScrollHeight = 0;
      }
    }
  }

  onMessagesScroll(event: Event): void {
    if (!this.scrollLoadEnabled) return;
    const el = event.target as HTMLDivElement;
    if (el.scrollTop <= 50 && this.hasMoreMessages && !this.isLoadingMore) {
      this.prevScrollHeight = el.scrollHeight;
      this.restoreScrollAfterLoad = true;
      this.ngZone.run(() => this.loadMore.emit());
    }
  }

  private scrollToBottom(): void {
    if (this.messagesContainer) {
      const el = this.messagesContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
      // Enable scroll-triggered load-more only after the initial render settles
      this.scrollEnableTimer = setTimeout(() => {
        this.scrollLoadEnabled = true;
      }, 400);
    }
  }

  ngOnDestroy(): void {
    if (this.scrollEnableTimer) clearTimeout(this.scrollEnableTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
